import { jsx, jsxs } from 'react/jsx-runtime';
import classNames from 'classnames';
import { forwardRef, useContext } from 'react';
import { getTestLabels } from '../../utils/getTestLabels.js';
import style from './Chip.module.scss.js';
import { ChipGroupContext } from './ChipGroupContext.js';

const { componentTestLabel, elementTestLabel } = getTestLabels('ActionChip');
/**
 * [design (Figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=5618-27371&node-type=frame&t=78j1b1D2r7OjbLWy-0)
 * | [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Chip/ActionChip.tsx)
 *
 * @param {object} [anchorProps] - (optional) Accepts any valid [native HTML anchor element attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attributes) including `data-*` attributes.'
 * @param {object} [buttonProps] - (optional) Accepts any valid [native HTML button element attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#attributes) including `data-*` attributes.'
 * @param {string} [emphasis='default'] - (optional) Can be used to change the outline.
 * @param {string} label - (required) Mandatory text for the chip.
 * @param {boolean} [selected] - (optional) Can be used to signify a "selected" or "current" state.
 */
const ActionChip = forwardRef((props, ref) => {
    const { className, anchorProps, buttonProps, emphasis = 'default', selected, label, ...otherProps } = props;
    const inChipGroup = useContext(ChipGroupContext);
    const element = typeof buttonProps === 'undefined' && typeof anchorProps !== 'undefined'
        ? 'a'
        : 'button';
    const classes = classNames(style['z-chip'], {
        [style[`z-chip--emphasis-${emphasis}`]]: emphasis !== 'default',
        [style[`z-chip--selected`]]: selected,
    });
    const textElement = (jsx("span", { className: style['z-chip__text'], "data-zta": elementTestLabel('Text'), children: label }));
    return (jsxs("div", { ref: ref, role: inChipGroup ? 'listitem' : undefined, className: classNames(className, style['z-chip-wrap'], style['z-chip--action']), "data-zta": componentTestLabel, ...otherProps, children: [element === 'a' && (jsx("a", { className: classes, ...anchorProps, children: textElement })), element === 'button' && (jsx("button", { className: classes, type: "button", ...buttonProps, children: textElement }))] }));
});
ActionChip.displayName = 'ActionChip';

export { ActionChip };
//# sourceMappingURL=ActionChip.js.map
